// The shell's two standing tools: preferences and help.
//
// They are panes rather than destinations. Changing a language and looking up a shortcut
// do not replace the work underneath, and neither has a durable record address. Every
// control in these panes is live; a setting the product does not yet support is not drawn.

import { LayoutDashboardIcon } from "lucide-react";
import { Link } from "react-router";
import { LOCALES, type Locale, useLanguage, useT } from "../shell/i18n";
import { useLocaleChoice } from "../shell/i18n/choice";
import { Action } from "../ui/button";
import { Labelled } from "../ui/field";
import { NativeSelect } from "../ui/native-select";
import { Pane } from "../ui/pane";

const NAMED: Record<Locale, "locale.en" | "locale.tr"> = {
  en: "locale.en",
  tr: "locale.tr",
};

interface ToolPaneProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function SettingsPane({
  open,
  onOpenChange,
  onPersonalize,
}: ToolPaneProps & { readonly onPersonalize: () => void }) {
  const t = useT();
  const language = useLanguage();
  const choose = useLocaleChoice();

  return (
    <Pane open={open} onOpenChange={onOpenChange} title={t("settings.heading")} reading>
      <div className="flex max-w-prose flex-col gap-6">
        <section className="flex flex-col items-start gap-3">
          <div>
            <h3 className="m-0 text-shell-title font-medium text-foreground">
              {t("personalization.heading")}
            </h3>
            <p className="m-0 mt-1 text-shell-body text-muted-foreground">
              {t("personalization.settingsHint")}
            </p>
          </div>
          <Action purpose="secondary" onClick={onPersonalize}>
            <LayoutDashboardIcon aria-hidden="true" />
            {t("personalization.start")}
          </Action>
        </section>
        <section className="flex flex-col gap-3 border-line-subtle border-t pt-6">
          <h3 className="m-0 text-shell-title font-medium text-foreground">
            {t("settings.languageHeading")}
          </h3>
          <Labelled label={t("locale.label")} hint={t("settings.languageHint")}>
            {(ids) => (
              <NativeSelect
                {...ids}
                value={language}
                disabled={!choose}
                onChange={(event) => choose?.(event.currentTarget.value as Locale)}
              >
                {LOCALES.map((locale) => (
                  <option key={locale} value={locale}>
                    {t(NAMED[locale])}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Labelled>
        </section>
      </div>
    </Pane>
  );
}

export function HelpPane({ open, onOpenChange }: ToolPaneProps) {
  const t = useT();

  return (
    <Pane open={open} onOpenChange={onOpenChange} title={t("help.heading")} reading>
      <div className="flex max-w-prose flex-col gap-6">
        <section className="flex flex-col gap-3">
          <h3 className="m-0 text-shell-title font-medium text-foreground">
            {t("help.shortcutsHeading")}
          </h3>
          <dl className="m-0 grid grid-cols-[1fr_auto] gap-x-4 gap-y-3 text-shell-body">
            <dt>{t("help.search")}</dt>
            <dd className="m-0">
              <kbd className="rounded border border-line-subtle bg-surface-panel-subtle px-2 py-1">
                Ctrl K
              </kbd>
            </dd>
            <dt>{t("help.close")}</dt>
            <dd className="m-0">
              <kbd className="rounded border border-line-subtle bg-surface-panel-subtle px-2 py-1">
                Esc
              </kbd>
            </dd>
          </dl>
        </section>
        <section className="flex flex-col gap-3">
          <h3 className="m-0 text-shell-title font-medium text-foreground">
            {t("help.inboxHeading")}
          </h3>
          <p className="m-0 text-shell-body text-muted-foreground">{t("help.inboxBody")}</p>
          <nav
            aria-label={t("help.inboxHeading")}
            className="flex flex-col items-start gap-2 text-shell-body"
          >
            <Link to="/inbox/tasks" onClick={() => onOpenChange(false)}>
              {t("nav.inbox.tasks")}
            </Link>
            <Link to="/inbox/messages/received" onClick={() => onOpenChange(false)}>
              {t("nav.inbox.messages")}
            </Link>
            <Link to="/inbox/notices" onClick={() => onOpenChange(false)}>
              {t("nav.inbox.notices")}
            </Link>
          </nav>
        </section>
      </div>
    </Pane>
  );
}
